import React from "react";
import { GlobalStyles } from "@mui/material";
import { IUsuario, useUsuario } from "../../../../../Hooks/UsuarioHook";
import LogoSinsoft from "../../../../../Assets/logo-sinsoft.png";
import { resumoFiltrosTexto } from "../../resumoFiltros";

interface RelatorioProps {
  status: string;
  servidor: string;
  sexo: string;
  zonaRural: string;
  idadeMin: string;
  idadeMax: string;
  profissao: string;
  bairro: string;
  cadastroInicio: string;
  cadastroFim: string;
  searchTerm: string;
  filteredUsuarios: IUsuario[];
  camposSelecionados: string[];
  /** Quando true, estiliza cada página como uma folha (sombra/espaçamento) — só na pré-visualização em tela. */
  modoPreview?: boolean;
}

const camposDisponiveis: Record<string, { label: string; get: (user: any) => string }> = {
  id: { label: "ID", get: (u) => u.id },
  nome: { label: "Nome", get: (u) => u.nome },
  cpf: { label: "CPF", get: (u) => u.cpf },
  cnpj: { label: "CNPJ", get: (u) => u.cnpj },
  email: { label: "Email", get: (u) => u.email },
  telefone: { label: "Telefone", get: (u) => u.telefone },
  sexo: { label: "Sexo", get: (u) => u.sexo },
  data_nascimento: { label: "Nascimento", get: (u) => formatarData(u.data_nascimento) },
  data_cadastro: { label: "Cadastro", get: (u) => formatarData(u.data_cadastro) },
  ultimo_login: { label: "Último Login", get: (u) => formatarData(u.ultimo_login) },
  perfil: { label: "Perfil", get: (u) => u.perfil },
  servidor: { label: "Servidor", get: (u) => (u.servidor ? "Sim" : "Não") },
  administrador: { label: "Administrador", get: (u) => (u.administrador ? "Sim" : "Não") },
  inativo: { label: "Inativo", get: (u) => formatarData(u.inativo) },
  descricao: { label: "Descrição", get: (u) => u.descricao },
  endereco: {
    label: "Endereço",
    get: (u) => (u.endereco ? `${u.endereco}${u.complemento ? ", " + u.complemento : ""}` : ""),
  },
  bairro: { label: "Bairro", get: (u) => u.bairro },
  cidade: { label: "Cidade", get: (u) => u.cidade },
  uf: { label: "UF", get: (u) => u.uf },
  cep: { label: "CEP", get: (u) => u.cep },
  zona_rural: { label: "Zona Rural", get: (u) => (u.zona_rural ? "Sim" : "Não") },
  localidade_rural: { label: "Localidade Rural", get: (u) => u.localidade_rural },
  observacoes: { label: "Observações", get: (u) => u.observacoes },
};

const LINHAS_POR_PAGINA = 18;

export const Relatorio = ({
  filteredUsuarios,
  camposSelecionados,
  status,
  servidor,
  sexo,
  zonaRural,
  idadeMin,
  idadeMax,
  profissao,
  bairro,
  cadastroInicio,
  cadastroFim,
  searchTerm,
  modoPreview = false,
}: RelatorioProps) => {
  const { usuario } = useUsuario();
  const agora = new Date();
  const dataFormatada = agora.toLocaleDateString("pt-BR");
  const horaFormatada = agora.toLocaleTimeString("pt-BR");
  const cnpj = formatarCnpj((usuario?.entidade as any)?.CNPJ);

  const totalPaginas = Math.max(1, Math.ceil(filteredUsuarios.length / LINHAS_POR_PAGINA));

  // resumo dos filtros aplicados — MESMA fonte dos chips da lista (resumoFiltros)
  const partesFiltro = resumoFiltrosTexto({
    searchTerm,
    status,
    servidor,
    sexo,
    zonaRural,
    idadeMin,
    idadeMax,
    profissao,
    bairro,
    cadastroInicio,
    cadastroFim,
  });

  const thStyle: React.CSSProperties = {
    padding: "6px 8px",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: "#fff",
    background: "#2563eb",
    textAlign: "left",
    whiteSpace: "nowrap",
  };

  const tdStyle: React.CSSProperties = {
    padding: "4px 8px",
    fontSize: 10.5,
    color: "#1e293b",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 220,
  };

  return (
    <div>
      <GlobalStyles
        styles={{
          "@page": { size: "landscape", margin: "0.8cm" },
          "@media print": {
            body: {
              WebkitPrintColorAdjust: "exact !important",
              printColorAdjust: "exact !important",
            },
          },
        }}
      />

      {Array.from({ length: totalPaginas }).map((_, paginaIndex) => {
        const start = paginaIndex * LINHAS_POR_PAGINA;
        const end = Math.min(start + LINHAS_POR_PAGINA, filteredUsuarios.length);
        const linhas = filteredUsuarios.slice(start, end);

        return (
          <div
            key={paginaIndex}
            style={{
              pageBreakAfter: paginaIndex < totalPaginas - 1 ? "always" : "auto",
              minHeight: "18.5cm",
              display: "flex",
              flexDirection: "column",
              fontFamily: "'Plus Jakarta Sans', Arial, sans-serif",
              color: "#1e293b",
              ...(modoPreview
                ? {
                    background: "#fff",
                    padding: "0.8cm",
                    boxShadow: "0 2px 10px rgba(15,23,42,0.18)",
                    marginBottom: paginaIndex < totalPaginas - 1 ? 20 : 0,
                    boxSizing: "border-box" as const,
                  }
                : {}),
            }}
          >
            {/* Cabeçalho */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "2px solid #2563eb",
                paddingBottom: 10,
                marginBottom: 10,
                gap: 12,
              }}
            >
              {/* Prefeitura: logo + nome + CNPJ */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                {usuario?.entidade?.logo && (
                  <img
                    src={usuario.entidade.logo}
                    alt="Logo"
                    style={{ width: 52, height: 52, objectFit: "contain" }}
                  />
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.2 }}>
                    {usuario?.entidade?.titulo}
                  </div>
                  {cnpj && (
                    <div style={{ fontSize: 10.5, color: "#64748b" }}>CNPJ: {cnpj}</div>
                  )}
                </div>
              </div>

              {/* Título + filtros */}
              <div style={{ textAlign: "center", flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>
                  Relatório de Usuários Cadastrados
                </div>
                {partesFiltro.length > 0 && (
                  <div style={{ fontSize: 9.5, color: "#64748b", marginTop: 2 }}>
                    {partesFiltro.join("  •  ")}
                  </div>
                )}
              </div>

              {/* Paginação + total */}
              <div style={{ textAlign: "right", fontSize: 10.5, whiteSpace: "nowrap" }}>
                <div style={{ fontWeight: 700 }}>
                  Página {paginaIndex + 1} de {totalPaginas}
                </div>
                <div style={{ color: "#64748b" }}>
                  Registros {start + 1} a {end} de {filteredUsuarios.length}
                </div>
              </div>
            </div>

            {/* Tabela */}
            <table style={{ width: "100%", borderCollapse: "collapse", flex: 1 }}>
              <thead>
                <tr>
                  {camposSelecionados.map((campo) => (
                    <th key={campo} style={thStyle}>
                      {camposDisponiveis[campo]?.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {linhas.map((user, i) => (
                  <tr
                    key={i}
                    style={{ background: i % 2 === 0 ? "#ffffff" : "#f5f7fb" }}
                  >
                    {camposSelecionados.map((campo) => (
                      <td key={campo} style={tdStyle}>
                        {camposDisponiveis[campo]?.get(user.usuario) ?? "-"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Rodapé */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderTop: "1px solid #cbd5e1",
                paddingTop: 8,
                marginTop: 10,
                fontSize: 10,
                color: "#64748b",
              }}
            >
              <div>
                Relatório gerado por <b style={{ color: "#334155" }}>{usuario?.usuario?.nome}</b> em{" "}
                {dataFormatada} às {horaFormatada}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span>Desenvolvido por</span>
                <img src={LogoSinsoft} alt="Sinsoft Informática" style={{ height: 16 }} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

function formatarData(data: string | Date | undefined): string {
  if (!data) return "";
  const d = new Date(data);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("pt-BR");
}

function formatarCnpj(v?: string): string {
  if (!v) return "";
  const d = String(v).replace(/\D/g, "");
  if (d.length !== 14) return String(v);
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3.$4-$5");
}
